/**
 * Chart colours for the Cases analytics tab.
 *
 * One mapping, shared by every chart on the tab, so a colour always means the
 * same thing. Values are the validated categorical slots (blue = slot 1,
 * orange = slot 2), which clear the colourblind-separation and contrast gates
 * as an adjacent pair in both light and dark. Do not substitute by eye — the
 * ordering is the safety mechanism, not decoration.
 *
 * Colour follows the ENTITY, never its rank: Quebec is always blue and Ontario
 * always orange, so filtering one region out never repaints the other.
 */

export interface CasesPalette {
    quebec: string;
    ontario: string;
    /** diverging poles for "before / after delivery" */
    before: string;
    after: string;
    /** ordinal ramp for age buckets, light -> dark (never lighter than step 250) */
    ramp: [string, string, string, string, string];
    grid: string;
    axis: string;
    muted: string;
    good: string;
    warning: string;
    serious: string;
    critical: string;
}

const LIGHT: CasesPalette = {
    quebec: '#2a78d6',
    ontario: '#eb6834',
    before: '#2a78d6',
    after: '#e34948',
    ramp: ['#86b6ef', '#5598e7', '#3987e5', '#256abf', '#184f95'],
    grid: '#e1e0d9',
    axis: '#c3c2b7',
    muted: '#898781',
    good: '#0ca30c',
    warning: '#fab219',
    serious: '#ec835a',
    critical: '#d03b3b',
};

const DARK: CasesPalette = {
    quebec: '#3987e5',
    ontario: '#d95926',
    before: '#3987e5',
    after: '#e66767',
    ramp: ['#184f95', '#256abf', '#3987e5', '#5598e7', '#86b6ef'],
    grid: '#2c2c2a',
    axis: '#383835',
    muted: '#898781',
    good: '#0ca30c',
    warning: '#fab219',
    serious: '#ec835a',
    critical: '#d03b3b',
};

export const casesPalette = (mode: 'light' | 'dark'): CasesPalette =>
    mode === 'dark' ? DARK : LIGHT;

/** Severity -> status colour. Always paired with a text label, never colour alone. */
export const severityColor = (p: CasesPalette, severity: string): string =>
    severity === 'critical' ? p.critical : severity === 'serious' ? p.serious : p.warning;

export const REGION_LABEL: Record<string, string> = {
    quebec: 'Quebec',
    ontario: 'Ontario',
};

export const STAGE_LABEL: Record<string, string> = {
    sent_to_signed: 'Contract sent → signed',
    signed_to_transport: 'Signed → transport',
    transport_to_cremation: 'Transport → cremation',
    cremation_to_ready: 'Cremation → ready',
    ready_to_scheduled: 'Ready → scheduled',
    scheduled_to_delivered: 'Scheduled → delivered',
};

export const WORKSTREAM_LABEL: Record<string, string> = {
    dec_form15: 'DEC / Form 15',
    benefits: 'Benefits (CPP/QPP)',
    ashes_delivery: 'Ashes delivery',
    aftercare: 'Aftercare',
};
